/**
 * @typedef {string} UUID
 */

/**
 * @typedef {Object} ListServiceResult
 * @template T
 * @property {Number} page
 * @property {T[]} list
 */

/**
 * @typedef {Object} ListServicePagingParams
 * @property {{page: Number, pageSize: Number, direction: 'ASC', sort: 'NAME', match: String | null}} pagingCriteria
 * @property {() => String} getPageParams
 */

/**
 * @typedef {Object} DestinationProfileService
 * @property {(destinationProfileType: InternalDestinationName) => angular.IHttpResponse<DestinationProfileModel>} createProfile Creates a new model to store info about destination profile fields.
 * @property {(profileId: UUID) => angular.IHttpResponse<DestinationProfileModel>} checkoutProfileById Creates a temporary editable copy of the referred destination profile.
 * @property {(profile: DestinationProfileModel) => angular.IHttpResponse<DestinationProfileModel>} commitProfileChanges Validates and saves the changes of the editable destination profiles.
 * @property {(profiles: DestinationProfileModel[]) => angular.IHttpResponse<Object>} deleteProfiles Deletes the given destination profiles.
 * @property {(profileId: UUID) => angular.IHttpResponse<DestinationProfileModel>} getProfileById Retrieves a destination profile by its ID.
 * @property {(endpoint : String, destinationProfilePagingParams : ListServicePagingParams) => angular.IHttpResponse<ListServiceResult<DestinationProfileModel>>} getList
 * @property {(destinationProfile : DestinationProfileModel, updatedDestinationProfile : DestinationProfileModel) => angular.IHttpResponse<DestinationProfileModel>} updateProfile Patch the destination profile with the differences between the two profiles.
 */

(function() {

  angular.module('DestinationProfileProvider', [])

  .factory('DestinationProfileModel', [function() {
    return function(data) {
      this.info = {...data};
      this.options = {
        isSelected: false
      };
      return this;
    }
  }])


  .factory('DestinationProfilePagingParams', ['DEFAULT_ITEMS_PER_PAGE', function(DEFAULT_ITEMS_PER_PAGE) {
    return function () {

      this.pagingCriteria = {
        page: 0,
        pageSize: DEFAULT_ITEMS_PER_PAGE,
        direction: 'ASC',
        sort: 'NAME',
        match: null,
        types: null
      }

      this.getPageParams = function () {
        var params =
          '?page=' + this.pagingCriteria.page +
          "&pageSize=" + this.pagingCriteria.pageSize +
          "&direction=" + this.pagingCriteria.direction +
          "&sort=" + this.pagingCriteria.sort;

        if (this.pagingCriteria.match) {
          params += "&match=" + this.pagingCriteria.match;
        }

        if (this.pagingCriteria.types) {
          params += "&types=" + this.pagingCriteria.types;
        }

        return params
      }

      return this
    }
  }])

  .service('DestinationProfileService', ['$http', '$q', 'TenantListService', 'Utils', 'DestinationProfileModel',
    /**
     * @type {DestinationProfileService}
     */
    function($http, $q, TenantListService, Utils, DestinationProfileModel) {

      this.getProfileById = function (destinationProfileId) {
        const deferred = $q.defer()
        const url = `${TenantListService.currentTenant.info._links["px:destinationProfiles"].href}/${destinationProfileId}`
        $http.get(url).then(function(response) {
          const destinationProfile = new DestinationProfileModel(response.data)
          deferred.resolve(destinationProfile);
        }).catch(function(error){
          deferred.reject(error);
        });
        return deferred.promise;
      };

      this.getList = function (endpoint, destinationProfilePagingParams) {
        const deferred = $q.defer()
        const url = endpoint? endpoint + destinationProfilePagingParams.getPageParams() : TenantListService.currentTenant.info._links['px:destinationProfiles'].href  + destinationProfilePagingParams.getPageParams()

        $http.get(url).then(function(response) {
          const listObject = {
            page: response.data.page,
            list: [],
          };
          if (response.data._embedded) {
            response.data._embedded['px:destinationProfiles'].forEach((response, index) => {
              const item = new DestinationProfileModel(response)
              listObject.list.push(item)
            })
          }
          deferred.resolve(listObject);
        }).catch(function(error){
          deferred.reject(error);
        });
        return deferred.promise;
      };

      this.createProfile = function (destinationProfileType) {
        var deferred = $q.defer()
        const body = {
          type: destinationProfileType
        }

        console.debug("Created destination profile: " + destinationProfileType);
        let createDestinationProfileUrl = "";
        const apiEndpoint = TenantListService.currentTenant.info.apiEndpoint;
        const tenantId = TenantListService.currentTenant.customProperties.id;
        // TODO-NA: We should change the External API to be able to create destination profile without Identity Provider! For now, we can use this hack.
        createDestinationProfileUrl = `https://${apiEndpoint}/v1/tenants/${tenantId}/identityProviders/00000000-0000-0000-0000-000000000000/directories/00000000-0000-0000-0000-000000000000/destinationProfiles`;

        $http.post(createDestinationProfileUrl, body).then(function(response) {
          const destinationProfile = new DestinationProfileModel(response.data)
          deferred.resolve(destinationProfile);
        }).catch(function(error){
          deferred.reject(error);
        });
        return deferred.promise;
      };

      this.updateProfile = function(destinationProfile, updatedDestinationProfile) {
        const patch = Utils.getObjectsDifferencesForPatching(destinationProfile.info, updatedDestinationProfile.info);
        const deferred = $q.defer();
        if (patch && patch.length > 0) {
          $http.patch(destinationProfile.info._links.self.href, patch).then((response) => {
            destinationProfile.info = response.data;
            deferred.resolve(destinationProfile);
          }).catch(function(error){
            deferred.reject(error);
          });
        } else {
          deferred.resolve(destinationProfile);
        }

        return deferred.promise;
      };

      this.deleteProfiles = function (destinationProfiles) {
        if (destinationProfiles && destinationProfiles.length > 0) {
          var promises = [];
          angular.forEach(destinationProfiles , function(destinationProfile) {
            const deferred = $q.defer();
            const url = destinationProfile.info._links.self.href;

            $http.delete(url).then(function(response) {
              deferred.resolve(response);
            }).catch(function(error){
              deferred.reject(error);
            });
            const promise = deferred.promise;
            promises.push(promise);
          });

          return $q.all(promises);
        } else {
          throw "DestinationProfileService.deleteProfiles: no destinationProfiles provided";
        }
      };

      this.commitProfileChanges = function (destinationProfile) {
        const commitProfileChangesUrl = destinationProfile.info._links["px:commitProfileChanges"].href;
        const deferred = $q.defer();

        $http.post(commitProfileChangesUrl).then(function(response) {
          const committedProfile = new DestinationProfileModel(response.data);
          deferred.resolve(committedProfile);
        }).catch(function(error){
          deferred.reject(error);
        });
        return deferred.promise;
      }

      this.checkoutProfileById = function (destinationProfileId) {
        const checkoutProfileUrl = `${TenantListService.currentTenant.info._links["px:destinationProfiles"].href}/${destinationProfileId}/checkout`
        const deferred = $q.defer();

        $http.post(checkoutProfileUrl).then(function(response) {
          const checkedOutProfile = new DestinationProfileModel(response.data);
          deferred.resolve(checkedOutProfile);
        }).catch(function(error){
          deferred.reject(error);
        });
        return deferred.promise;
      }

      return this;
    }
  ])

  .factory('ListPagingParams', ['DEFAULT_ITEMS_PER_PAGE', function(DEFAULT_ITEMS_PER_PAGE) {
    return function () {

      this.pagingCriteria = {
        page: 0,
        pageSize: DEFAULT_ITEMS_PER_PAGE,
        direction: 'ASC',
        sort: 'NAME',
        match: null,
      }

      this.getPageParams = function () {
        var params =
          '?page=' + this.pagingCriteria.page +
          "&pageSize=" + this.pagingCriteria.pageSize +
          "&direction=" + this.pagingCriteria.direction +
          "&sort=" + this.pagingCriteria.sort;

        if (this.pagingCriteria.match) {
          params += "&match=" + this.pagingCriteria.match;
        }

        return params
      }

      return this
    }
  }])

  .factory('pxDataModel', ['$http', '$q', 'TenantListService','Utils',
    function($http, $q, TenantListService, Utils) {
    return function(data) {
      this.info = {...data};

      if (this.info.field === 'docLib') {
        this.info.field = 'documentLibrary'
      }
      return this
    }
  }])

  .service('SharePointListService', ['$http', '$q', 'pxDataModel',
   function($http, $q, pxDataModel) {

      this.getList = function (url, listPagingParams) {
        var deferred = $q.defer();
        if (url) {
          var params = listPagingParams.getPageParams();
          $http.get(url + params).then(function(response) {
            const listObject = {
              page: response.data.page,
              list: response.data._embedded ? response.data._embedded['px:fieldChoices'].map(item => new pxDataModel(item)) : [],
              mapType: 'fields'
            };
            deferred.resolve(listObject);
          }).catch(function(error){
            deferred.reject(error);
          });
        } else {
          deferred.resolve();
        }

        return deferred.promise;
      }

    }
  ])

  .factory('DestinationsModel', ['$filter', 'RegexModel', function($filter, RegexModel) {

    /**
     * Creates a field for destination profile name editing.
     * @param {String} value The value for the profile name field.
     * @returns {DestinationProfileField}
     */
    const createProfileNameField = function(value) {
      return {
        value: value,
        error: null,
        validate: function() {
          if (!this.value || this.value.length == 0) {
            this.error = "MustBeSpecified";
          } else if (this.value.match(illegalNameCheck)) {
            this.error = "NotAllowedCharacters";
          }
        }
      }
    };

    const regexModel = new RegexModel();
    const urlCheck = regexModel.urlRegex();
    const illegalNameCheck = regexModel.badNameString().regex;

    return function(NetworkListService) {

      this.SharePoint = {
        internalName: 'SharePoint',
        displayName: 'Microsoft SharePoint Online',
        path:'app/templates/sharepointLogo.template.html',
        helpUrl: 'https://docshield.tungstenautomation.com/Printix/en_US/help/admin/Printix_admin/t_capture_destination_profiles.html',
        identityProviderName: 'azure',
        templateName: 'sharepointDestinationProfileForm.template.html',
        createProfileModel: function(destinationProfile) {
          return {
            fields: {
              name: createProfileNameField(destinationProfile.info.name),

              site: {
                error: null,
                validate: function() {
                  if (!destinationProfile.info.site || destinationProfile.info.site.length === 0) {
                    this.error = "MustBeSpecified";
                  }
                }
              },

              documentLibrary: {
                error: null,
                validate: function() {
                  if (!destinationProfile.info.documentLibrary || destinationProfile.info.documentLibrary.length === 0) {
                    this.error = "MustBeSpecified";
                  }
                }
              },
            }
          }
        },
        getAdminConsentUrl: function(identityProvider) {
          const enableUrl = identityProvider._links['px:enable_sharepoint'].href;
          const redirect = enableUrl + '{domainUrl}/admin/app/components/authenticationSettings/MSIdentity/accept.html';
          return redirect;
        },
        isAccessGranted: function(directory) {
          return directory && directory['sharepoint_granted'];
        }
      };

      this.OneDriveForBusiness = {
        internalName: 'OneDriveForBusiness',
        displayName: 'Microsoft OneDrive',
        path:'app/templates/onedriveLogo.template.html',
        helpUrl: 'https://docshield.tungstenautomation.com/Printix/en_US/help/admin/Printix_admin/t_capture_destination_profiles.html',
        identityProviderName: 'azure',
        templateName: 'onedriveDestinationProfileForm.template.html',
        createProfileModel: function(destinationProfile) {
          return {
            fields: {
              name: createProfileNameField(destinationProfile.info.name),
            }
          }
        },
        getAdminConsentUrl: (identityProvider) => {
          const enableUrl = identityProvider._links['px:enable_onedrive'].href;
          const redirect = enableUrl + '{domainUrl}/admin/app/components/authenticationSettings/MSIdentity/accept.html';
          return redirect;
        },
        isAccessGranted: function(directory) {
          return directory && directory['onedrive_granted'];
        }
      },

      this.Connector = {
        internalName: 'Connector',
        displayName: 'Connector',
        id: 'Connector',
        path:'app/templates/connectorLogo.template.html',
        helpUrl: 'https://docshield.tungstenautomation.com/Printix/en_US/help/admin/Printix_admin/t_capture_destination_profiles.html',
        templateName: 'connectorDestinationProfileForm.template.html',

        /**
         * Create a new model for Connector destinations.
         *
         * @param {DestinationProfileModel} destinationProfile
         * @returns {DestinationModel}
         */
        createProfileModel: function(destinationProfile) {

          const SECRET_KEY_PREFIX_LENGTH = 4;
          const SECRET_KEY_SEPARATOR = ',';
          const SECRET_KEY_MASKING_CHARACTER = '*';

          const keyLengths = {
            HMAC_SHA256: 32,
            HMAC_SHA512: 64,
            TOKEN_512: 64,
          };

          const signatureAlgorithms = {
            HMAC_SHA256: { id: 'HMAC_SHA256', name: 'HMAC SHA-256' },
            HMAC_SHA512: { id: 'HMAC_SHA512', name: 'HMAC SHA-512' },
            TOKEN_512: { id: 'TOKEN_512', name: $filter('translate')('ConnectorToken') }
          };

          const model = {
            fields: {
              name: createProfileNameField(destinationProfile.info.name),

              connectorUrl: {
                value: destinationProfile.info.connectorUrl,
                error: null,
                validate: function() {
                  if (!this.value || this.value.length === 0) {
                    this.error = 'MustBeSpecified';
                  } else if (!this.value.match(urlCheck)) {
                    this.error = 'MustBeValidURL';
                  }
                }
              },

              networkId: {
                value: destinationProfile.info.networkId,
                error: null,
                selectedItem: null,
                validate: function() {
                  this.value = this.selectedItem ? this.selectedItem.customProperties.id : '';
                },
                clear: function() {
                  this.selectedItem = null;
                  this.error = null;
                }
              },

              secretKeys: {
                value: null,
                error: null,
                items: (destinationProfile.info.secretKeys?.map(value => ({
                    // Note: If the Base-64 encoded secret key's length is 44 character, it means the string represents 32 bytes.
                    // If the secret key is 32 byte long, it means that it's an HMAC SHA-256 key.
                    algorithm: value.length === 44 ? "HMAC_SHA256" : "HMAC_SHA512",
                    value: value
                  })) ?? []).concat(
                    destinationProfile.info.tokens?.map(value => ({
                      algorithm: "TOKEN_512",
                      value: value
                  })) ?? []),
                newItem: null,
                validate: function(model) {
                  if (!model.fields.secretKeys.error) {
                    // Validate the secret key list
                    if (!model.fields.secretKeys.items || model.fields.secretKeys.items.length === 0) {
                      model.fields.secretKeys.value = null;
                      model.fields.secretKeys.error = 'MustHaveAtLeastOneSharedSecret';
                    } else {
                      model.fields.secretKeys.value = model.fields.secretKeys.items
                          .filter(i => i.algorithm !== "TOKEN_512")
                          .map(i => i.value)
                          .join(SECRET_KEY_SEPARATOR);
                      model.fields.tokens.value = model.fields.secretKeys.items
                          .filter(i => i.algorithm === "TOKEN_512")
                          .map(i => i.value)
                          .join(SECRET_KEY_SEPARATOR);
                    }
                  }

                  if (model.fields.secretKeys.error) {
                    // If there was any error, move the focus to the input field to allow the admin to fix the provided value
                    inputElem.focus();
                  }
                }
              },

              tokens: {
                value: null,
                error: null,
              }
            },

            signatureAlgorithms: signatureAlgorithms,

            commitNewSecret: function() {
              console.log("Add new secret...");
              const inputElem = angular.element('#newSecretKey');
              let newSecretBytes = null;
              /** @type {string} */
              const newSecretText = inputElem.length > 0 ? inputElem[0].value : null;
              if (newSecretText) {
                try {
                  newSecretBytes = atob(newSecretText);
                  if (btoa(newSecretBytes) !== newSecretText) {
                    // If the decoded bytes of the Base-64 text are encoded back to Base-64, we should get the original Base-64 encoded text.
                    // When this is not true, it means that some padding characters have been removed from the end of the original Base-64
                    // encoded text. This is considered an error.
                    newSecretBytes = null;
                  }
                } catch (error) {
                  console.error(error);
                }
              }

              // Validate the newly provided secret key
              const expectedLengthInBytes = keyLengths[this.fields.secretKeys.newItem.algorithm.id];
              if (!newSecretText || newSecretText.length === 0 || !expectedLengthInBytes) {
                // There is no text to insert on the clipboard.
                this.fields.secretKeys.error = null;
                this.fields.secretKeys.newItem = null;
              } else if (!newSecretBytes || newSecretBytes.length !== expectedLengthInBytes) {
                this.fields.secretKeys.error = 'ProvidedValueIsNotSecretKey';
              } else if (this.fields.secretKeys.items.some(secret => secret.value === newSecretText)) {
                this.fields.secretKeys.error = 'ProvidedValueIsAlreadyInTheList';
              } else {
                // If the inserted value seems to be a valid shared secret, we add it to the list
                this.fields.secretKeys.error = null;
                this.fields.secretKeys.items.push({
                  algorithm: this.fields.secretKeys.newItem.algorithm.id,
                  value: newSecretText
                });
                this.fields.secretKeys.newItem = null;
              }
            },

            canGenerateNewSecret: function() {
              return this.fields.secretKeys.items.length < 5;
            },

            createNewSecret: function() {
              const DEFAULT_ALGORITHM = "HMAC_SHA256";
              this.fields.secretKeys.newItem = {
                algorithm: this.signatureAlgorithms[DEFAULT_ALGORITHM],
                value: ''
              };
              this.generateSecret();
            },

            generateSecret: function() {
              const length = keyLengths[this.fields.secretKeys.newItem.algorithm.id];
              if (!length) {
                console.error("Unsupported signature algorithm: " + this.fields.secretKeys.newItem.algorithm.id);
                return;
              }

              /** @type {number[]} */
              const result = Array(length);
              let newSecret = '';

              do {

                // Generate a new random secret key as Base-64 encoded string.
                for (let i = 0; i < length; ++i) {
                  result[i] = Math.floor(256 * Math.random());
                }
                newSecret = btoa(String.fromCharCode(...result));

                // Verify that the first few characters from the newly generated secret key is unique. This is necessary
                // because later, only the first few character will be shown and all other characters will be masked.
                // If the first few characters would not be unique, we could not distinguish the secret keys.
                const prefix = newSecret.substring(0, SECRET_KEY_PREFIX_LENGTH);
                if (this.fields.secretKeys.items.find(secret => secret.value.startsWith(prefix))) {
                  newSecret = null;
                }

              } while (!newSecret);

              this.fields.secretKeys.error = null;
              this.fields.secretKeys.newItem.value = newSecret;
            },

            inputFocused: function() {
              const inputElem = angular.element('#newSecretKey');
              inputElem[0].value = this.fields.secretKeys.newItem.value;
              inputElem.focus();
              inputElem.select();
            },

            cancelNewSecret: function() {
              this.fields.secretKeys.newItem = null;
              this.fields.secretKeys.error = null;
            },

            hasAnyNewSecret: function() {
              return this.fields.secretKeys.items.find(secret => !this.isMaskedSecret(secret));
            },

            /**
             * Copies the given secret to the clipboard.
             *
             * @param {{algorithm: String, value: String}} secretToCopy The secret to copy.
             */
            copySecret: function(secretToCopy) {
              navigator.clipboard.writeText(secretToCopy.value);
            },

            deleteSecret: function(secretToRemove) {
              const index = this.fields.secretKeys.items.findIndex(secret => secret === secretToRemove);
              if (index !== -1) {
                this.fields.secretKeys.error = null;
                this.fields.secretKeys.items.splice(index, 1);
              }
            },

            /**
             * Verifies if the provided secret key contains masking characters.
             *
             * @param {{algorithm: String, value: String}} secretToCheck The secret key to check.
             * @returns {Boolean} True, if the secret key contains masking characters; otherwise, false.
             */
            isMaskedSecret: function(secretToCheck) {
              return secretToCheck?.value && secretToCheck.value.indexOf(SECRET_KEY_MASKING_CHARACTER) > -1;
            },

            onSignatureAlgorithmChanged: function() {
              this.fields.secretKeys.error = null;
              //this.fields.secretKeys.items = [];
              this.generateSecret();
              this.inputFocused();
            },
          };

          if (NetworkListService && model.fields.networkId.value) {
            NetworkListService.getNetworkById(model.fields.networkId.value).then(
                network => {
                  model.fields.networkId.selectedItem = network;
                }).catch(function(error) {
                    console.error("Network not found", error);
                    model.fields.networkId.error = "SelectedNetworkNotFound_SpecifyOrUnset";
                });
          }

          return model;
        },
      }

      return this;
    };
  }])

})();
